import { FieldError, RangeSlider, Typography } from '@/ui-kit/';
import styles from './setting-info-registration-form.module.scss';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { debounce } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { type Profile } from '@/types/profile';
import { ButtonSubmitStep } from '../button-submit-step';
import { callYaMetrika } from '@/utils/call-ya-metrika';
import { useToast } from '@/store/useToast';
import { SuccessNotificationIcon } from '@/assets/icons';
import {
    registrationStepFiveSchema,
    RegistrationStepFiveType
} from '@/schemes/registrationV2/registration-step-five-schema';
import clsx from 'clsx';
import { Writeable, ZodEnum, ZodOptional } from 'zod';
import { eventsApi } from '@/api';
import { FieldBlock } from '@/components/field-block';

const DELAY_SEND_DATA = 1000;

interface Props {
    userData?: Profile | null;
    onUpdateUserData: (data: { gender?: 'male' | 'female' }) => Promise<any>;
    nextStep: () => void;
}

export const SettingInfoRegistrationForm: React.FC<Props> = ({ userData, onUpdateUserData, nextStep }) => {
    const showToast = useToast((state) => state.show);
    useEffect(() => {
        callYaMetrika('new_dating_settings_screen');
        eventsApi.updateUserEvent('new_dating_settings_screen');
    }, []);

    const initialValues = useMemo(() => {
        const getMinAge = () => {
            if (!userData) return 18;
            return userData.min_age !== null ? (userData.min_age >= 60 ? 60 : userData.min_age) : 18;
        };

        const getMaxAge = () => {
            if (!userData) return 24;
            return userData.max_age !== null ? (userData.max_age >= 60 ? 60 : userData.max_age) : 24;
        };

        
        return {
            gender: userData?.gender,
            looks_for: userData?.looks_for || 'any',
            min_age: getMinAge(),
            max_age: getMaxAge(),
        };
    }, [userData]);

    const [isLooksForSelected, setIsLooksForSelected] = useState<Profile['looks_for']>(initialValues.looks_for);


    const [isLoading, setIsLoading] = useState(false);

    const methods = useForm<RegistrationStepFiveType>({
        resolver: zodResolver(registrationStepFiveSchema),
        defaultValues: initialValues,
    });

    const {
        handleSubmit,
        formState: { errors, isDirty, isValid },
    } = methods;

    const onSubmit = async (data: RegistrationStepFiveType) => {
        try {
            setIsLoading(true);
            /*
             * for update only dirty data use code below
             * if (isDirty) {
             * 
             *     await onUpdateUserData({ state: 'complete', ...data });
             * }
             */
            await onUpdateUserData(data);

            nextStep();
            eventsApi.updateUserEvent('registration_step_complete_dating_settings_screen');
        } catch (error) {
            console.log(error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleChangeRangeAge = (value: any) => {
        methods.setValue('min_age', value[0]);
        methods.setValue('max_age', value[1]);
        eventsApi.updateUserEvent('registration_min_max_age');
        handleChangeUserDataDebounce({ min_age: value[0], max_age: value[1] });
    };

    const handleChangeUserDataDebounce = useCallback(
        debounce(async (data: any) => {
            await onUpdateUserData(data);
        }, DELAY_SEND_DATA),
        [onUpdateUserData],
    );

    const handleChangeGender = (value: any) => {
        methods.setValue('gender', value);
        handleChangeUserDataDebounce({ gender: value });
    };

    const handleChangeLooksFor = (value: any) => {
        methods.setValue('looks_for', value);
        setIsLooksForSelected(value);
        handleChangeUserDataDebounce({ looks_for: value });
    };

    useEffect(() => {
        eventsApi.updateUserEvent('registration_step_view_dating_settings_screen');
    }, [])

    return (
        <FormProvider {...methods}>
            <form
                className={styles.form}
                onSubmit={handleSubmit(onSubmit)}>
                <div className={styles.formContainer}>
                    <Typography
                        variant="p"
                        fontWeight="medium"
                        className={styles.titleForm}
                        center>
                        Настройки дейтинга
                    </Typography>

                    <Controller
                        name="gender"
                        control={methods.control}
                        render={({ field: { onChange } }) =>
                            <FieldBlock>
                                <div className={styles.formLabel}>
                                    <Typography
                                        variant="label"
                                        fontWeight="bold"
                                        className={styles.formGenderTitle}
                                        isError={!!errors?.gender?.message}>
                                        Твой пол
                                    </Typography>
                                    <p className={styles.formGenderText}>
                                        Позже можно изменить его один раз
                                    </p>
                                </div>
                                <div className={styles.genderWrapper}>
                                    <div
                                        className={clsx(styles.genderBtn, methods.getValues('gender') === 'female' && styles.genderBtnActive)}
                                        onClick={() => {
                                            const val = 'female'
                                            onChange(val)
                                            handleChangeGender(val)
                                        }}>
                                        Женский
                                    </div>

                                    <div
                                        className={clsx(styles.genderBtn, methods.getValues('gender') === 'male' && styles.genderBtnActive)}
                                        onClick={() => {
                                            const val = 'male'
                                            onChange(val)
                                            handleChangeGender(val)
                                        }}
                                    >
                                        Мужской
                                    </div>
                                </div>
                            </FieldBlock>
                        } />

                    <FieldBlock>
                        <div className={styles.formGenderSearch}>
                            <div className={styles.formLabel}>
                                <Typography
                                    variant="label"
                                    fontWeight="bold"
                                    className={styles.formGenderTitle}
                                    isError={!!errors?.gender?.message}
                                >
                                    Кого тебе показывать?
                                </Typography>
                                <p className={styles.formGenderText}>
                                    Можно изменить в любой момент
                                </p>
                            </div>
                            <div className={styles.genderWrapper}>
                                <div
                                    className={clsx(
                                        styles.genderBtn,
                                        isLooksForSelected === 'female' && styles.genderBtnActive,
                                    )}
                                    onClick={() => handleChangeLooksFor('female')}>
                                    Женщин
                                </div>

                                <div
                                    className={clsx(
                                        styles.genderBtn,
                                        isLooksForSelected === 'male' && styles.genderBtnActive,
                                    )}
                                    onClick={() => handleChangeLooksFor('male')}>
                                    Мужчин
                                </div>

                                <div
                                    className={clsx(
                                        styles.genderBtn,
                                        isLooksForSelected === 'any' && styles.genderBtnActive,
                                    )}
                                    onClick={() => handleChangeLooksFor('any')}>
                                    Неважно
                                </div>
                            </div>
                        </div>
                    </FieldBlock>

                    <FieldBlock>
                        <div className={styles.formLabel}>
                            <Typography
                                variant="label"
                                fontWeight="bold"
                                className={styles.formGenderTitle}
                                isError={!!errors?.gender?.message}>
                                Возрастной диапазон
                            </Typography>
                        </div>
                        <RangeSlider
                            maxStart={initialValues?.max_age}
                            minStart={initialValues?.min_age}
                            onChange={handleChangeRangeAge} />
                        <FieldError message={errors?.max_age?.message || errors?.min_age?.message} />
                    </FieldBlock>
                </div>
                <ButtonSubmitStep
                    isLoading={isLoading}
                    stepNumber={1}
                    isDisabled={!isValid}
                />
            </form>
        </FormProvider>
    );
};
